import { CheckOutlined, DownloadOutlined, MoreOutlined, RollbackOutlined } from '@ant-design/icons';
import { IssueCaseDoc, IssueTaskDoc } from '@gooduncles/gu-app-schema';
import { FloatButton, Popconfirm } from 'antd';
import { FC, useMemo } from 'react';

import { downloadJSON } from 'src/utils/common';
import { batchOpenIssueTasks, batchPendingIssueTasks, hasOpenTasks, hasPendingTasks } from 'src/utils/issue/issue-util';

interface IssueBatchActionButtonsProps {
  issueCases: IssueCaseDoc[];
  tasksByCase: Record<string, IssueTaskDoc[]>;
}

const IssueBatchActionButtons: FC<IssueBatchActionButtonsProps> = ({ issueCases, tasksByCase }) => {
  const pendingInfo = useMemo(() => hasPendingTasks(tasksByCase), [tasksByCase]);
  const openInfo = useMemo(() => hasOpenTasks(tasksByCase), [tasksByCase]);

  const totalTasksCount = useMemo(
    () => Object.values(tasksByCase).reduce((sum, tasks) => sum + tasks.length, 0),
    [tasksByCase],
  );
  const hasData = issueCases.length > 0 || totalTasksCount > 0;

  const handleBatchRegister = async () => {
    await batchOpenIssueTasks(tasksByCase);
  };

  const handleBatchCancel = async () => {
    await batchPendingIssueTasks(tasksByCase);
  };

  const handleDownload = () => {
    const data = { issueCases, tasksByCase };
    const timestamp = new Date().toISOString().slice(0, 10);
    downloadJSON(data, `issue-data-${timestamp}`);
  };

  return (
    <FloatButton.Group trigger='click' icon={<MoreOutlined />} style={{ position: 'absolute', right: 8, bottom: 12 }}>
      <Popconfirm
        title='일괄 등록'
        description={`${pendingInfo.pendingTasksCount}개의 이슈 태스크를 등록하시겠습니까?`}
        onConfirm={handleBatchRegister}
        disabled={!pendingInfo.hasPending}
        okButtonProps={{ disabled: !pendingInfo.hasPending }}
        okText='등록'
        cancelText='아니오'
      >
        <FloatButton
          type='primary'
          icon={<CheckOutlined />}
          style={{
            opacity: pendingInfo.hasPending ? 1 : 0.4,
            cursor: pendingInfo.hasPending ? 'pointer' : 'not-allowed',
          }}
        />
      </Popconfirm>
      <Popconfirm
        title='일괄 등록 취소'
        description={`${openInfo.openTasksCount}개의 이슈 태스크를 등록 취소하시겠습니까?`}
        onConfirm={handleBatchCancel}
        disabled={!openInfo.hasOpen}
        okButtonProps={{ disabled: !openInfo.hasOpen }}
        okText='등록 취소'
        cancelText='아니오'
      >
        <FloatButton
          icon={<RollbackOutlined />}
          style={{
            opacity: openInfo.hasOpen ? 1 : 0.4,
            cursor: openInfo.hasOpen ? 'pointer' : 'not-allowed',
          }}
        />
      </Popconfirm>
      <Popconfirm
        title='데이터 다운로드'
        description={`이슈 케이스 ${issueCases.length}건, 태스크 ${totalTasksCount}건을 다운로드하시겠습니까?`}
        onConfirm={handleDownload}
        disabled={!hasData}
        okButtonProps={{ disabled: !hasData }}
        okText='다운로드'
        cancelText='아니오'
      >
        <FloatButton
          icon={<DownloadOutlined />}
          style={{
            opacity: hasData ? 1 : 0.4,
            cursor: hasData ? 'pointer' : 'not-allowed',
          }}
        />
      </Popconfirm>
    </FloatButton.Group>
  );
};

export default IssueBatchActionButtons;
export type { IssueBatchActionButtonsProps };
